import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { AdSetTargetingNode, CampaignSummary } from '../../../types/metaAdResources';
import type { TargetingConfig } from '../../../types/metaAdDraft';
import { detectTargetingShape } from '../../../lib/metaTargetingInterpret';
import { getAdTargeting, getAdTree, getCampaigns } from '../api';
import { adTreeDateWindow, pickExtractionAd } from '../extraction';
import { TargetingPreviewDetails } from './TargetingPreviewDetails';

type Applied = { targeting: TargetingConfig; campaignId: string; campaignName: string };

type Props = {
  accountId: string;
  currency: string | null;
  onApply: (a: Applied) => void;
  onClose: () => void;
};

// What we're showing: the campaign list, an ad-set chooser (multi-ad-set
// campaigns), the in-flight extraction, or the extracted preview ready to apply.
type Stage =
  | { kind: 'list' }
  | { kind: 'adsets'; campaign: CampaignSummary; adSets: AdSetTargetingNode[] }
  | { kind: 'extracting'; campaign: CampaignSummary }
  | {
      kind: 'preview';
      campaign: CampaignSummary;
      adName: string | null;
      adStatus: string | null;
      adSetName: string | null;
      targeting: TargetingConfig;
    }
  | { kind: 'error'; campaign: CampaignSummary; message: string };

function daysAgo(iso: string | null): string {
  if (!iso) return 'no ads';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'no ads';
  const d = Math.floor(ms / 86_400_000);
  if (d <= 0) return 'today';
  if (d === 1) return '1 day ago';
  return `${d} days ago`;
}

function formatSpend(spend: number | null, currency: string | null): string | null {
  if (spend == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 0,
    }).format(spend);
  } catch {
    return `${spend}`;
  }
}

export function TargetingExtractionModal({ accountId, currency, onApply, onClose }: Props) {
  const [activeOnly, setActiveOnly] = useState(true);
  const [daysSince, setDaysSince] = useState(90);
  const [stage, setStage] = useState<Stage>({ kind: 'list' });

  const campaigns = useQuery({
    queryKey: ['metaAdCampaigns', accountId, activeOnly, daysSince],
    queryFn: () => getCampaigns(accountId, { activeOnly, daysSince }).then((r) => r.campaigns),
    enabled: accountId.trim().length > 0,
  });

  // Campaign chosen → fetch the tree. One ad set (or none) → extract straight
  // through; multiple → let the AM pick which ad set's audience (A/B campaigns
  // deliberately differ per ad set).
  async function chooseCampaign(campaign: CampaignSummary) {
    setStage({ kind: 'extracting', campaign });
    try {
      const tree = await getAdTree(accountId, campaign.id, adTreeDateWindow(campaign));
      if (tree.adSets.length > 1) {
        setStage({ kind: 'adsets', campaign, adSets: tree.adSets });
        return;
      }
      await extractFromAdSets(campaign, tree.adSets, null);
    } catch {
      setStage({ kind: 'error', campaign, message: "Couldn't read this campaign's targeting. Try again." });
    }
  }

  // Extract from a specific set of ad sets (one chosen, or the whole campaign when
  // there's a single ad set). Stamps the chosen ad set's name/id onto targeting.
  async function extractFromAdSets(
    campaign: CampaignSummary,
    adSets: AdSetTargetingNode[],
    chosen: AdSetTargetingNode | null,
  ) {
    setStage({ kind: 'extracting', campaign });
    try {
      const pick = pickExtractionAd({ campaignId: campaign.id, campaignName: campaign.name, status: null, adSets });
      if (pick.kind === 'no-ads') {
        setStage({ kind: 'error', campaign, message: 'This campaign has no ads to extract targeting from.' });
        return;
      }
      const { targeting, found } = await getAdTargeting(accountId, pick.ad.id);
      if (!found) {
        setStage({ kind: 'error', campaign, message: 'This ad has no targeting set. Try a different campaign.' });
        return;
      }
      setStage({
        kind: 'preview',
        campaign,
        adName: pick.ad.name,
        adStatus: pick.ad.status,
        adSetName: chosen?.adSetName ?? null,
        targeting: { ...targeting, sourceAdSetId: chosen?.adSetId ?? null, sourceAdSetName: chosen?.adSetName ?? null },
      });
    } catch {
      setStage({ kind: 'error', campaign, message: "Couldn't read this campaign's targeting. Try again." });
    }
  }

  function apply() {
    if (stage.kind !== 'preview') return;
    onApply({ targeting: stage.targeting, campaignId: stage.campaign.id, campaignName: stage.campaign.name });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Extract targeting from existing campaign</DialogTitle>
          <DialogDescription>
            Pick a previous Lead Gen or Traffic campaign to copy its audience into this draft.
          </DialogDescription>
        </DialogHeader>

        {/* ---------- list ---------- */}
        {stage.kind === 'list' && (
          <>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!activeOnly}
                  onChange={(e) => setActiveOnly(!e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Show all campaigns
              </label>
              <label className={['flex items-center gap-2', activeOnly ? '' : 'opacity-40'].join(' ')}>
                Active in the last
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={daysSince}
                  disabled={!activeOnly}
                  onChange={(e) => setDaysSince(Math.min(Math.max(Number(e.target.value) || 90, 1), 365))}
                  className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                />
                days
              </label>
            </div>

            <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200">
              {campaigns.isLoading && (
                <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading campaigns…
                </div>
              )}
              {campaigns.isError && (
                <div className="p-4 text-sm">
                  <p className="text-destructive">Couldn't load campaigns.</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => campaigns.refetch()}>
                    Retry
                  </Button>
                </div>
              )}
              {campaigns.data && campaigns.data.length === 0 && (
                <p className="p-4 text-sm text-slate-500">
                  No campaigns match the filters. Try adjusting the days range or “Show all”.
                </p>
              )}
              {campaigns.data?.map((c) => {
                const spend = formatSpend(c.recentSpend, currency);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => chooseCampaign(c)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">{c.name}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        {c.status && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">{c.status}</span>
                        )}
                        <span
                          className={[
                            'rounded px-1.5 py-0.5',
                            c.isLeadGen ? 'bg-kyck-50 text-kyck-700' : 'bg-slate-100',
                          ].join(' ')}
                        >
                          {c.isLeadGen ? 'Lead Gen' : 'Traffic / other'}
                        </span>
                        <span>Last ad {daysAgo(c.latestAdAt)}</span>
                        {c.adCount != null && <span>· {c.adCount} ads</span>}
                        {spend && <span>· {spend} spend</span>}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ---------- ad set chooser (multi-ad-set campaigns) ---------- */}
        {stage.kind === 'adsets' && (
          <>
            <p className="text-sm text-slate-600">
              “{stage.campaign.name}” has multiple ad sets — pick whose audience to copy.
            </p>
            <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
              {stage.adSets.map((adSet, idx) => {
                const noAds = adSet.ads.length === 0;
                return (
                  <div key={adSet.adSetId ?? idx} className="flex items-center justify-between gap-3 px-4 py-3">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {adSet.adSetName ?? 'Unnamed ad set'}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        {adSet.status && <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">{adSet.status}</span>}
                        <span>{adSet.ads.length} ads</span>
                        {noAds && <span className="text-slate-400">— no ads to extract from</span>}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={noAds}
                      onClick={() => extractFromAdSets(stage.campaign, [adSet], adSet)}
                    >
                      Pick this targeting
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setStage({ kind: 'list' })}>
              Back to campaigns
            </Button>
          </>
        )}

        {/* ---------- extracting ---------- */}
        {stage.kind === 'extracting' && (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading targeting from “{stage.campaign.name}”…
          </div>
        )}

        {/* ---------- error ---------- */}
        {stage.kind === 'error' && (
          <div className="space-y-3 p-2">
            <p className="text-sm text-destructive">{stage.message}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStage({ kind: 'list' })}>
                Back to campaigns
              </Button>
              <Button variant="outline" size="sm" onClick={() => chooseCampaign(stage.campaign)}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* ---------- preview ---------- */}
        {stage.kind === 'preview' && (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <p>
                Extracted from:{' '}
                <span className="font-medium text-slate-900">
                  {[stage.campaign.name, stage.adSetName].filter(Boolean).join(' / ')}
                </span>
              </p>
              <p>
                Source ad:{' '}
                <span className="font-medium text-slate-900">{stage.adName ?? 'Unnamed ad'}</span>
                {stage.adStatus && <span className="capitalize text-slate-500"> ({stage.adStatus})</span>}
              </p>
            </div>
            {(() => {
              const raw = stage.targeting.rawPayload;
              const shape = detectTargetingShape(raw);
              return (
                <>
                  <TargetingPreviewDetails rawPayload={raw} />
                  {shape === 'meta-native' && (
                    <p className="text-xs text-slate-400">Targeting will be applied exactly as set in Meta.</p>
                  )}
                </>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStage({ kind: 'list' })}>
                Back
              </Button>
              <Button onClick={apply}>Apply targeting</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
